import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	dxtToLocalServerTime,
	ErrorModal,
	MesfModal,
} from "@dexteel/mesf-core";
import { CircularProgress, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
	FormInfo,
	FormInstance,
	FormInstanceResponse,
} from "../models/FormInstance";
import { FormQuestion } from "../models/FormQuestion";
import { FormSection } from "../models/FormSection";
import {
	getFormInstance,
	submitFormInstance,
} from "../repositories/FormsRepository";
import { Questions } from "./Questions";

interface Responses {
	[key: number]:
		| {
				value: string | number | null | undefined;
				isAnswered: boolean;
		  }
		| undefined;
}

export const useFormInstanceById = (
	formInstanceId: number | null,
	checkAnswers: boolean,
) => {
	return useQuery<FormInstanceResponse[], Error>({
		queryKey: ["formInstance", formInstanceId, checkAnswers],
		queryFn: ({ signal }) => {
			if (formInstanceId) {
				return getFormInstance({ formInstanceId, checkAnswers }, signal);
			}
			return [];
		},
	});
};

type Props = {
	show: "create" | "edit" | "view" | "delete" | "";
	onHide: (shouldUpdate: boolean) => void;
	formInstanceId: number | null;
	refetchForms: () => void;
};

export const EditInstance = ({
	show,
	onHide,
	formInstanceId,
	refetchForms,
}: Props) => {
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isInSubmitSection, setIsInSubmitSection] = useState(false);
	const [canNext, setCanNext] = useState(false);

	const [form, setForm] = useState<FormInfo | null>(null);
	const [formSections, setFormSections] = useState<FormSection[]>([]);
	const [currentSection, setCurrentSection] = useState<FormSection | null>(
		null,
	);

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
		refetch,
	} = useFormInstanceById(
		formInstanceId ?? null,
		show === "view" || isInSubmitSection,
	);

	const { reset, control, watch } = useForm<FormInstance>();
	const comments = watch("Comments");

	const handleClose = () => {
		onHide(false);
		setIsInSubmitSection(false);
		setCurrentSection(formSections[0]);
	};

	const handleSubmit = async () => {
		setIsSubmitLoading(true);

		const resp = await submitFormInstance({
			formInstanceId: formInstanceId!,
			comments,
		});

		if (resp.ok) {
			onHide(true);
		} else {
			setError(resp.message);
			onHide(false);
		}

		setFormSections([]);
		setIsInSubmitSection(false);
		setIsSubmitLoading(false);
	};

	const formatFormInstance = useMemo(() => {
		return (rowsToFormat: FormInstanceResponse[]) => {
			if (!rowsToFormat || rowsToFormat.length === 0) return null;

			const firstRow = rowsToFormat[0];
			const formInstanceData: FormInstance = {
				FormInstanceId: firstRow.FormInstanceId,
				FormId: firstRow.FormId,
				AssetId: firstRow.AssetId,
				ShiftId: firstRow.ShiftId,
				OpenTimestamp: firstRow.OpenTimestamp,
				SubmitTimestamp: firstRow.SubmitTimestamp,
				SubmitUser: firstRow.SubmitUser,
				StatusCode: firstRow.FormInstanceStatusCode,
				Comments: firstRow.FormInstanceComments,
			};

			const formData: FormInfo = {
				FormId: firstRow.FormId,
				FormName: firstRow.FormName,
				IsEnabled: firstRow.FormIsEnabled,
				LayoutCode: firstRow.LayoutCode,
				IncludesComment: firstRow.FormIncludesComment,
				IsActive: firstRow.FormIsActive,
			};

			const questionsMap = new Map<number, FormQuestion>();
			rowsToFormat.forEach((row) => {
				if (row.QuestionId && !questionsMap.has(row.QuestionId)) {
					questionsMap.set(row.QuestionId, {
						QuestionId: row.QuestionId,
						QuestionText: row.QuestionText,
						QuestionTypeCode: row.QuestionTypeCode,
						IsRequired: row.IsRequired,
						SortOrder: row.QuestionSortOrder,
						NextQuestionId:
							row.QuestionTypeCode === "R" ? null : row.NextQuestionId,
						NextQuestion: null,
						IsFirstQuestion: row.IsFirstQuestion,
						FlowLevel: row.FlowLevel,
						FlowPath: row.FlowPath,
						IsAnswered: row.IsAnswered,
						AnswerId: row.AnswerId,
						RadioButtonSelected: row.RadioButtonSelected,
						TextBoxContent: row.TextBoxContent,
						NumericalContent: row.NumericalContent,
						DatetimeContent: row.DatetimeContent,
						AnswerComments: row.AnswerComments,
						NotAnswered: row.NotAnswered,
						Warning: row.Warning,
						Options: [],
					});
				}
			});

			questionsMap.forEach((question) => {
				if (question.NextQuestionId) {
					question.NextQuestion =
						questionsMap.get(question.NextQuestionId) || null;
				}
			});

			const sectionsMap = new Map<number, FormSection>();

			rowsToFormat.forEach((row) => {
				const sectionId = row.SectionId;

				if (!sectionsMap.has(sectionId) && row.SectionIsActive) {
					sectionsMap.set(sectionId, {
						SectionId: sectionId,
						SectionName: row.SectionName,
						SortOrder: row.SectionSortOrder,
						Subtitle: row.SectionSubtitle,
						BackgroundColor: row.SectionBackgroundColor,
						Questions: [],
						IsActive: row.SectionIsActive,
					});
				}

				const section = sectionsMap.get(sectionId)!;
				const question = questionsMap.get(row.QuestionId);

				if (
					question &&
					section &&
					!section.Questions.find((q) => q.QuestionId === question.QuestionId)
				) {
					section.Questions.push(question);
				}

				if (row.OptionText && row.FlowOptionId && question) {
					const existingOption = question.Options.find(
						(opt) => opt.OptionId === row.FlowOptionId,
					);

					if (!existingOption) {
						const nextQuestion = row.NextQuestionId
							? questionsMap.get(row.NextQuestionId) || null
							: null;

						question.Options.push({
							OptionId: row.FlowOptionId,
							OptionText: row.OptionText,
							OptionTypeCode: row.OptionTypeCode || "",
							SortOrder: row.OptionSortOrder || 0,
							IsEnabled: row.OptionIsEnabled || false,
							NextQuestionId: row.NextQuestionId,
							NextQuestion: nextQuestion,
						});
					}
				}
			});

			const sections = Array.from(sectionsMap.values())
				.map((section) => ({
					...section,
					Questions: Array.from(section.Questions.values())
						.sort((a, b) => a.SortOrder - b.SortOrder)
						.map((question) => ({
							...question,
							Options: question.Options.sort(
								(a, b) => a.SortOrder - b.SortOrder,
							),
						})),
				}))
				.sort((a, b) => a.SortOrder - b.SortOrder);

			return {
				formInstance: formInstanceData,
				form: formData,
				sections,
			};
		};
	}, [isInSubmitSection]);

	useEffect(() => {
		if (isError) {
			setError(e?.message);
		}
	}, [isError, e]);

	useEffect(() => {
		if (isSuccess) {
			const rowsFormatted = formatFormInstance(rows);
			reset(rowsFormatted?.formInstance);
			setForm(rowsFormatted?.form ?? null);
			setFormSections(rowsFormatted?.sections ?? []);

			const actualSection = rowsFormatted?.sections.find((s) => {
				if (currentSection?.SectionId) {
					return s.SectionId === currentSection.SectionId;
				} else {
					return s.Questions && s.Questions.some((q) => !q.IsAnswered);
				}
			});

			if (actualSection) {
				setCurrentSection(actualSection);
			} else {
				if (rowsFormatted?.sections) {
					setCurrentSection(
						rowsFormatted.sections[rowsFormatted.sections.length - 1],
					);
					setIsInSubmitSection(true);
				} else setCurrentSection(null);
			}
		}
	}, [isSuccess, rows]);

	useEffect(() => {
		refetch();
	}, [show, isInSubmitSection]);

	const renderQuestionResponse = (
		question: FormQuestion,
		currentResponses?: Responses | FormInstanceResponse[],
	) => {
		let response: string | number | null | undefined;
		const isViewMode = Array.isArray(currentResponses);

		if (isViewMode) {
			const viewQuestion = currentResponses.find(
				(r) => r.QuestionId === question.QuestionId,
			);
			if (!viewQuestion) return null;

			if (viewQuestion.DatetimeContent) {
				response = dxtToLocalServerTime(
					`${viewQuestion.DatetimeContent}`,
					"MM/dd/yyyy",
				);
			} else if (viewQuestion.RadioButtonSelected) {
				const selectedOption = question.Options?.find(
					(option) =>
						option.OptionId === Number(viewQuestion.RadioButtonSelected),
				);
				response = selectedOption ? selectedOption.OptionText : null;
			} else if (viewQuestion.NumericalContent) {
				response = viewQuestion.NumericalContent;
			} else if (viewQuestion.TextBoxContent) {
				response = viewQuestion.TextBoxContent;
			}
			if (response == undefined && !viewQuestion.IsAnswered) return null;
		} else {
			const responseEntry = (currentResponses as Responses)[
				question.QuestionId
			];
			response = responseEntry?.value;
			if (question.QuestionTypeCode === "R") {
				const selectedOption = question.Options?.find(
					(option) => option.OptionId === Number(response as string),
				);
				response = selectedOption ? selectedOption.OptionText : null;
			}
			const isAnswered = responseEntry?.isAnswered;
			if (response == undefined && !isAnswered) return null;
		}

		return (
			<div style={{ display: "flex" }}>
				<Typography color="textSecondary">{question.QuestionText}: </Typography>
				<Typography
					style={{ fontWeight: "bold", marginLeft: 5 }}
					color={response === undefined ? "error" : "inherit"}
				>
					{response !== undefined ? (response?.toString() ?? "") : "N/A"}
				</Typography>
			</div>
		);
	};

	return (
		<>
			<MesfModal
				maxWidth="lg"
				open={show === "edit" || show === "view"}
				handleClose={handleClose}
				title={`Form: ${form?.FormName || ""}`}
				key={"edit_form_instance"}
			>
				<form>
					<MesfModal.Content
						dividers
						style={{
							minWidth: "600px",
							maxHeight: "70vh",
							overflowY: "scroll",
						}}
					>
						{isLoading ? (
							<CenteredLazyLoading />
						) : show === "view" || isInSubmitSection ? (
							<Grid2 container justifyContent="space-between" spacing={1}>
								{formSections.map((section, i) => (
									<Grid2
										size={{
											md:
												formSections.length % 2 !== 0 &&
												i === formSections.length - 1
													? 12
													: 6,
										}}
										style={{
											padding: 20,
											borderRadius: 5,
											border: `2px solid ${section.BackgroundColor}`,
										}}
									>
										<Typography variant="h6" color="textPrimary">
											{section.SectionName}
										</Typography>
										{section.Questions.map((question) =>
											renderQuestionResponse(question, rows || []),
										)}
									</Grid2>
								))}
								{form?.IncludesComment && (
									<Grid2 size={{ md: 12, xs: 12 }} style={{ marginTop: 10 }}>
										<Controller
											name="Comments"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													label="Additional Comments"
													variant="outlined"
													fullWidth
													value={value || ""}
													onChange={onChange}
													disabled={show === "view"}
												/>
											)}
										/>
									</Grid2>
								)}
							</Grid2>
						) : (
							<Questions
								formSections={formSections}
								currentSection={currentSection}
								isSubmitLoading={isSubmitLoading}
								formInstanceId={formInstanceId!}
								setIsSubmitLoading={setIsSubmitLoading}
								setCanNext={setCanNext}
								onSuccess={(error?: string) => {
									if (error) {
										setError(error);
									} else {
										refetch();
										refetchForms();
									}
								}}
							/>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "0", margin: "5px 0" }}>
						{show === "view" ? (
							<Grid2
								size={{ md: 12 }}
								container
								spacing={2}
								style={{ margin: "0 15px" }}
								alignItems="center"
								justifyContent="center"
							>
								<Grid2 size={{ md: 3, xs: 12 }}>
									<Button
										variant="contained"
										color="inherit"
										onClick={handleClose}
										fullWidth
									>
										Close
									</Button>
								</Grid2>
							</Grid2>
						) : !isInSubmitSection ? (
							<Grid2
								size={{ md: 12 }}
								container
								spacing={2}
								style={{ margin: "0 15px" }}
								alignItems="center"
								justifyContent="center"
							>
								{currentSection && formSections.indexOf(currentSection) > 0 && (
									<Grid2 size={{ xs: 12, md: 4 }}>
										<Button
											variant="text"
											color="inherit"
											onClick={() => {
												if (currentSection) {
													const newSectionIndex =
														formSections.indexOf(currentSection);
													setCurrentSection(formSections[newSectionIndex - 1]);
												}
											}}
											fullWidth
										>
											Previous
										</Button>
									</Grid2>
								)}
								<Grid2 size={{ xs: 12, md: 4 }}>
									<Button
										variant="contained"
										color="primary"
										disabled={!canNext}
										fullWidth
										onClick={() => {
											if (currentSection) {
												if (
													formSections[formSections.length - 1].SectionId ===
													currentSection.SectionId
												) {
													setIsInSubmitSection(true);
												} else {
													const newSectionIndex =
														formSections.indexOf(currentSection);
													setCurrentSection(formSections[newSectionIndex + 1]);
												}
											}
										}}
									>
										{formSections[formSections.length - 1]?.SectionId ===
										currentSection?.SectionId
											? "Check answers"
											: "Next"}
									</Button>
								</Grid2>
							</Grid2>
						) : (
							isInSubmitSection && (
								<Grid2
									container
									size={{ md: 12 }}
									spacing={2}
									style={{ margin: "0 15px" }}
									alignItems="center"
									justifyContent="space-between"
								>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="inherit"
											onClick={() => {
												if (currentSection) {
													const newSectionIndex =
														formSections.indexOf(currentSection);
													setIsInSubmitSection(false);
													if (newSectionIndex === formSections.length - 1) {
														return;
													}
													setCurrentSection(formSections[newSectionIndex - 1]);
												}
											}}
										>
											Back
										</Button>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											onClick={handleSubmit}
											variant="contained"
											color="primary"
										>
											Submit
										</Button>
									</Grid2>
								</Grid2>
							)
						)}
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
